import { FC, ReactNode, useCallback, useMemo } from 'react';

import { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import { formatCurrency, formatPercent } from 'configs/columns';
import { DateTime } from 'luxon';
import { DataDisplayTable } from 'ui-kit';

import { SaleBidderLevel, SortableField } from '__generated__/globalTypes';

import { SalePoolLoans } from 'query/__generated__/salePoolLoans';

export type SalePoolLoan = SalePoolLoans['salePoolLoans']['loans'][number];
// DataDisplayTable's row type requires last_updated_date; a loan row has no such field (same
// placeholder as SaleOfferingsTable's).
type SalePoolLoanRow = SalePoolLoan & { last_updated_date: string };

export interface SalePoolLoanSortState {
  field: SortableField;
  desc: boolean;
}

interface LoanColumn {
  id: keyof SalePoolLoan;
  header: string;
  numeric?: boolean;
  display: (loan: SalePoolLoan) => ReactNode;
}

const text = (value: string | null) => value || '-';
const count = (value: number | null) => (value === null ? '-' : value.toLocaleString());
const date = (seconds: number | string | null) => {
  if (seconds === null || seconds === '') return '-';
  const parsed = DateTime.fromSeconds(Number(seconds), { zone: 'utc' });
  return parsed.isValid ? parsed.toFormat('M/d/yyyy') : '-';
};

export const LEVEL_1_COLUMNS: LoanColumn[] = [
  {
    id: 'account_id',
    header: 'Loan ID',
    display: (loan) => text(loan.account_id),
  },
  { id: 'sale_key', header: 'Sale Key', display: (loan) => text(loan.sale_key) },
  {
    id: 'fund_number',
    header: 'Fund #',
    display: (loan) => text(loan.fund_number),
  },
  {
    id: 'institution_id',
    header: 'Institution',
    display: (loan) => text(loan.institution_id),
  },
  {
    id: 'performance_status',
    header: 'Performance',
    display: (loan) =>
      loan.performance_status === 'NON_PERFORMING'
        ? 'Non-performing'
        : loan.performance_status === 'PERFORMING'
          ? 'Performing'
          : '-',
  },
  {
    id: 'asset_class',
    header: 'Asset Class',
    display: (loan) => text(loan.asset_class),
  },
  {
    id: 'product',
    header: 'Product',
    display: (loan) => text(loan.product),
  },
  {
    id: 'state',
    header: 'State',
    display: (loan) => text(loan.state),
  },
  {
    id: 'current_balance_cents',
    header: 'UPB',
    numeric: true,
    display: (loan) => formatCurrency(loan.current_balance_cents),
  },
  {
    id: 'original_balance_cents',
    header: 'Orig. Bal',
    numeric: true,
    display: (loan) => formatCurrency(loan.original_balance_cents),
  },
  {
    id: 'interest_rate_bps',
    header: 'Interest Rate',
    numeric: true,
    display: (loan) => formatPercent(loan.interest_rate_bps, 3),
  },
  {
    id: 'origination_date_seconds',
    header: 'Originated',
    display: (loan) => date(loan.origination_date_seconds),
  },
  {
    id: 'first_payment_date_seconds',
    header: 'First Payment',
    display: (loan) => date(loan.first_payment_date_seconds),
  },
  {
    id: 'next_payment_date_seconds',
    header: 'Next Payment',
    display: (loan) => date(loan.next_payment_date_seconds),
  },
  {
    id: 'maturity_date_seconds',
    header: 'Matures',
    display: (loan) => date(loan.maturity_date_seconds),
  },
  {
    id: 'loan_terms_months',
    header: 'Term',
    numeric: true,
    display: (loan) => count(loan.loan_terms_months),
  },
  {
    id: 'remaining_loan_terms_months',
    header: 'Remaining',
    numeric: true,
    display: (loan) => count(loan.remaining_loan_terms_months),
  },
  {
    id: 'age_months',
    header: 'Loan Age',
    numeric: true,
    display: (loan) => count(loan.age_months),
  },
  {
    id: 'days_past_due',
    header: 'Days Past Due',
    numeric: true,
    display: (loan) => count(loan.days_past_due),
  },
  {
    id: 'ltv_bps',
    header: 'LTV',
    numeric: true,
    display: (loan) => formatPercent(loan.ltv_bps, 2),
  },
];

// The contract's PII set, credit score and DTI included: shown at L2 only.
export const LEVEL_2_PII_COLUMNS: LoanColumn[] = [
  { id: 'loan_number', header: 'Loan Number', display: (loan) => text(loan.loan_number) },
  { id: 'asset_name', header: 'Asset Name', display: (loan) => text(loan.asset_name) },
  { id: 'borrower_name', header: 'Borrower', display: (loan) => text(loan.borrower_name) },
  { id: 'borrower_type', header: 'Borrower Type', display: (loan) => text(loan.borrower_type) },
  {
    id: 'borrower_summary',
    header: 'Borrower Summary',
    display: (loan) => text(loan.borrower_summary),
  },
  {
    id: 'borrower_revolving_debt_cents',
    header: 'Revolving Debt',
    numeric: true,
    display: (loan) => formatCurrency(loan.borrower_revolving_debt_cents),
  },
  {
    id: 'guarantor_names',
    header: 'Guarantors',
    display: (loan) => (loan.guarantor_names?.length ? loan.guarantor_names.join(', ') : '-'),
  },
  { id: 'collateral_name', header: 'Collateral', display: (loan) => text(loan.collateral_name) },
  {
    id: 'collateral_summary',
    header: 'Collateral Summary',
    display: (loan) => text(loan.collateral_summary),
  },
  {
    id: 'borrower_credit_score',
    header: 'Credit Score',
    numeric: true,
    display: (loan) => count(loan.borrower_credit_score),
  },
  {
    id: 'dti_bps',
    header: 'DTI',
    numeric: true,
    display: (loan) => formatPercent(loan.dti_bps, 2),
  },
  { id: 'auto_vin', header: 'VIN', display: (loan) => text(loan.auto_vin) },
  {
    id: 'property_description',
    header: 'Property',
    display: (loan) => text(loan.property_description),
  },
];

const isSortableField = (id: string): id is SortableField =>
  Object.prototype.hasOwnProperty.call(SortableField, id);

const columnDefsFor = (
  columns: LoanColumn[],
  allowedSortFields: ReadonlySet<SortableField>,
): ColumnDef<SalePoolLoanRow, any>[] =>
  columns.map((column) => ({
    id: column.id,
    accessorKey: column.id,
    header: column.header,
    enableSorting: isSortableField(column.id) && allowedSortFields.has(column.id),
    cell: (props) => <span>{column.display(props.row.original)}</span>,
    meta: { numeric: column.numeric ?? false, alignment: 'left' },
  }));

export const SalePoolLoanTable: FC<{
  loans: SalePoolLoan[];
  level: SaleBidderLevel;
  allowedSortFields: readonly SortableField[];
  totalCount: number;
  page: { pageNumber: number; pageSize: number };
  onPageChange: (pageNumber: number, pageSize: number) => void;
  sort: SalePoolLoanSortState | null;
  onSortChange: (sort: SalePoolLoanSortState | null) => void;
}> = ({ loans, level, allowedSortFields, totalCount, page, onPageChange, sort, onSortChange }) => {
  const rows: SalePoolLoanRow[] = useMemo(
    () => loans.map((loan) => ({ ...loan, last_updated_date: '' })),
    [loans],
  );
  const sortable = useMemo(() => new Set(allowedSortFields), [allowedSortFields]);
  const columns = useMemo(
    () =>
      columnDefsFor(
        level === SaleBidderLevel.L2
          ? [...LEVEL_1_COLUMNS, ...LEVEL_2_PII_COLUMNS]
          : LEVEL_1_COLUMNS,
        sortable,
      ),
    [level, sortable],
  );

  const sortingState: SortingState = useMemo(
    () => (sort && sortable.has(sort.field) ? [{ id: sort.field, desc: sort.desc }] : []),
    [sort, sortable],
  );

  const onSortingChanged: OnChangeFn<SortingState> = useCallback(
    (update) => {
      const next = typeof update === 'function' ? update(sortingState) : update;
      const id = next[0]?.id;
      onSortChange(id && isSortableField(id) ? { field: id, desc: next[0].desc } : null);
    },
    [onSortChange, sortingState],
  );

  const updateParams = useCallback(
    (params: { pageNumber?: number; pageSize?: number }) =>
      onPageChange(params.pageNumber ?? page.pageNumber, params.pageSize ?? page.pageSize),
    [onPageChange, page.pageNumber, page.pageSize],
  );

  return (
    <DataDisplayTable
      data={rows}
      columns={columns}
      noDataMessage="No loans in this pool."
      getRowId={(loan) => loan.id}
      stylingOptions={{ hoverRowColor: 'none', supportsLightTheme: true }}
      columnPinningLeft={['account_id']}
      sorting={{ state: sortingState, onSortingChanged, isManual: true }}
      pagination={{
        updateParams,
        queryParams: { filters: [], sortings: [], ...page },
        totalItems: totalCount,
      }}
    />
  );
};
